import React, { useState, useContext, createContext, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider, googleLogout } from '@react-oauth/google';

import {getBackendURL} from './utils';
const AuthContext = createContext();

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function AuthProviderInner({ children }) {
  const [user, setUser] = useState(() => {
    const saved = window.localStorage.getItem('ptc_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [accessToken, setAccessToken] = useState(() => window.localStorage.getItem('ptc_access_token'));
  const [refreshToken, setRefreshToken] = useState(() => window.localStorage.getItem('ptc_refresh_token'));

  function isTokenExpired(token) {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }

  const tryRefreshToken = async () => {
    if (!refreshToken) return false;
    try {
      const res = await fetch(getBackendURL() + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data && data.access_token) {
        setAccessToken(data.access_token);
        window.localStorage.setItem('ptc_access_token', data.access_token);
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token);
          window.localStorage.setItem('ptc_refresh_token', data.refresh_token);
        }
        const payload = parseJwt(data.access_token);
        if (payload && payload.sub) {
          const userObj = {
            ...data.user, // Use the user object from the backend response
            email: payload.sub,
            role: payload.role,
            user_id: data.user_id ?? payload.user_id // Prefer backend user_id, fallback to JWT if present
          };
          delete userObj.hashed_password; //Ensure sensitive fields that wont be here anyway are not stored just in case
          setUser(userObj);
          window.localStorage.setItem('ptc_user', JSON.stringify(userObj));
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const specialSignup = async ({ google_credential, phone }) => {
        const res = await fetch(getBackendURL() + '/auth/special', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: google_credential, 
              phone: phone, 
              role:  "trainer" }),
        });

        if (!res.ok) return { success: false };
        const data = await res.json();
        if (!data || !data.access_token) return { success: false };
        setAccessToken(data.access_token);
        window.localStorage.setItem('ptc_access_token', data.access_token);
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token);
          window.localStorage.setItem('ptc_refresh_token', data.refresh_token);
        }
        const payload = parseJwt(data.access_token);
        const userObj = {
          ...data.user, // Use the user object from the backend response
          email: payload && payload.sub ? payload.sub : undefined,
          role: payload && payload.role ? payload.role : undefined,
          user_id: data.user_id ?? (payload && payload.user_id ? payload.user_id : undefined)
        };
        delete userObj.hashed_password; //Ensure sensitive fields that wont be here anyway are not stored just in case
        setUser(userObj);
        window.localStorage.setItem('ptc_user', JSON.stringify(userObj));
        return { success: true };


  };

  const login = async ({ username, password, google_credential, role }) => {
    if (google_credential) {
      try {
        const res = await fetch(getBackendURL() + '/auth/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: google_credential, role: role || "trainer" }),
        });

        if (!res.ok) return { success: false };

        
        const data = await res.json();
        if (data && data.access_token) {
          setAccessToken(data.access_token);
          window.localStorage.setItem('ptc_access_token', data.access_token);
          if (data.refresh_token) {
            setRefreshToken(data.refresh_token);
            window.localStorage.setItem('ptc_refresh_token', data.refresh_token);
          }
          const payload = parseJwt(data.access_token);
          const userObj = {
            ...data.user, // Use the user object from the backend response
            email: payload && payload.sub ? payload.sub : undefined,
            role: payload && payload.role ? payload.role : undefined,
            user_id: data.user_id ?? (payload && payload.user_id ? payload.user_id : undefined)
          };
          delete userObj.hashed_password; //Ensure sensitive fields that wont be here anyway are not stored just in case
          setUser(userObj);
          window.localStorage.setItem('ptc_user', JSON.stringify(userObj));
          return { success: true };
        }
        return { success: false };
      } catch (e) {
        return { success: false };
      }
    }

    if (username === 'trainer' && password === '1234') {
      const res = await fetch(getBackendURL() + '/auth/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'trainer' }),
      });
      if (res.ok) {
        const data = await res.json();
        setAccessToken(data.access_token);
        window.localStorage.setItem('ptc_access_token', data.access_token);
        setRefreshToken(data.refresh_token);
        window.localStorage.setItem('ptc_refresh_token', data.refresh_token);
        setUser(data.user);
        window.localStorage.setItem('ptc_user', JSON.stringify(data.user));
        return { success: true };
      }
      return { success: false };
    }

    if (username === 'client' && password === '1234') {
      const res = await fetch(getBackendURL() + '/auth/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'client' }),
      });
      if (res.ok) {
        const data = await res.json();
        setAccessToken(data.access_token);
        window.localStorage.setItem('ptc_access_token', data.access_token);
        setRefreshToken(data.refresh_token);
        window.localStorage.setItem('ptc_refresh_token', data.refresh_token);
        setUser(data.user);
        window.localStorage.setItem('ptc_user', JSON.stringify(data.user));
        return { success: true };
      }
      return { success: false };
    }

    // window.location.href = 'https://<YOUR-AUTHENTIK-URL>/application/o/authorize/?client_id=<CLIENT_ID>&response_type=code&scope=openid+profile+email&redirect_uri=' + encodeURIComponent(window.location.origin + '/');
    return { success: false };
  };

  const logout = () => {
    const res = window.confirm('Are you sure you want to log out?');
    if (!res) return;

    window.localStorage.removeItem('ptc_user');
    window.localStorage.removeItem('ptc_access_token');
    window.localStorage.removeItem('ptc_refresh_token');
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    googleLogout();
    window.location.href = '/'; // Redirect to login page
  };

  const deleteAccountHandler = async () => {
    if (!accessToken) return false;

    // Ask for confirmation
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return false;
    }


    try {
      const res = await fetch(getBackendURL() + '/user/' + user.id + '?user_id=' + user.id, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (res.ok) {
        logout();
        alert('Your account has been deleted. We\'re sorry to see you go!');
        // redirect to home page
        window.location.href = '/';
        return true;
      } else {
        console.error('Failed to delete account');
        alert('Sorry, there was an error deleting your account. This is not intentional. I can delete your account myself if you can please email  info@heldsolutionsllc.com.');
        return false;
      }
    } catch (err) {
      console.error('Sorry - error deleting account:', err);

      // Apologize to user
      alert('Sorry, there was an error deleting your account. This is not intentional. I can delete your account myself if you can please email info@heldsolutionsllc.com.');
      return false;
    }
  };


  const pureFetchUserInfo = useCallback(async () => {

        const res = await fetch(getBackendURL() + '/me', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        console.log("Fetch user info response status: ", res.status, "using access token: ", accessToken);

        if (res.ok) {
          const data = await res.json();
          const updatedUser = {
            ...data,
            // email: data.email || user?.email,
            // role: data.role || user?.role,
            // user_id: data.user_id || user?.user_id,
          };
          return updatedUser;
        }
        return null;
  }, [accessToken, user]);

  useEffect(() => {
    if (accessToken && isTokenExpired(accessToken)) {
      tryRefreshToken().then(success => {
        if (!success) logout();
      });
    }
    // eslint-disable-next-line
  }, []);

  const fetchUserInfo = useCallback(async () => {
      if (!accessToken) return;

      try {
        const updatedUser = await pureFetchUserInfo();
        if (updatedUser) {
          setUser(updatedUser);
          window.localStorage.setItem('ptc_user', JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
    }, [accessToken, user]);

  useEffect(() => {

    fetchUserInfo();
    // eslint-disable-next-line
  }, []);

  return (
    <AuthContext.Provider value={{ 
            user, 
            login, 
            specialSignup,
            logout, 
            accessToken, 
            deleteAccountHandler, 
            pureFetchUserInfo,
            fetchUserInfo 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthProvider({ children }) {
  return (
    <GoogleOAuthProvider clientId="751555675946-aiev23sdt8i3pc9fg59f93j7kdvj31td.apps.googleusercontent.com">
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    </GoogleOAuthProvider>
  );
}

function useAuth() {
  return useContext(AuthContext);
}

export { AuthProvider, useAuth, parseJwt };